import type { QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

/**
 * Live updates via native EventSource (doc 14 ADR-08). One stream per active
 * business; named events map to TanStack Query invalidations.
 *
 * Hardened for real-world networks (the "have to refresh to see new messages"
 * class of bugs): EventSource only auto-reconnects when the socket dies LOUDLY.
 * A Wi-Fi→cellular hop, laptop sleep, or NAT timeout kills it silently — the
 * browser thinks it's connected and nothing ever arrives. So:
 *  - the server sends a named `ping` every 25s; a watchdog reconnects after
 *    >65s of total silence (2 missed pings + slack),
 *  - waking the tab (visibility/focus/online) refetches immediately and
 *    reconnects if the stream is closed or stale,
 *  - every (re)open refetches, closing the gap between fetch and stream start.
 */
export function useSse(businessId: string, qc: QueryClient): void {
  useEffect(() => {
    let es: EventSource | null = null;
    let lastSeen = Date.now();
    let disposed = false;

    const refreshAll = () => {
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
      // Prefix match — refetches whichever thread is open (missed its messages too).
      qc.invalidateQueries({ queryKey: ["thread"] });
    };

    const invalidate = (evt: MessageEvent) => {
      let conversationId: number | undefined;
      try {
        conversationId = JSON.parse(evt.data)?.conversationId;
      } catch {
        /* non-JSON */
      }
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
      if (conversationId != null) qc.invalidateQueries({ queryKey: ["thread", conversationId] });
    };

    const connect = () => {
      if (disposed) return;
      es?.close();
      es = new EventSource(`/api/v1/events?business=${encodeURIComponent(businessId)}`);
      lastSeen = Date.now();
      const touch = () => {
        lastSeen = Date.now();
      };

      es.addEventListener("ping", touch);
      es.addEventListener("message.created", (e) => {
        touch();
        invalidate(e);
      });
      es.addEventListener("conversation.updated", (e) => {
        touch();
        invalidate(e);
      });
      es.addEventListener("message.status", (e) => {
        touch();
        invalidate(e);
      });
      // Broadcast send/delivery progress → refresh the Broadcasts list + open drawer.
      es.addEventListener("broadcast.progress", (e: MessageEvent) => {
        touch();
        qc.invalidateQueries({ queryKey: [businessId, "broadcasts"] });
        try {
          const id = JSON.parse(e.data)?.broadcastId;
          if (id != null) qc.invalidateQueries({ queryKey: ["broadcast", id] });
        } catch {
          /* non-JSON */
        }
      });
      // Store automations firing (order confirmations etc.) → refresh run counters.
      es.addEventListener("automation.fired", () => {
        touch();
        qc.invalidateQueries({ queryKey: [businessId, "automations"] });
        qc.invalidateQueries({ queryKey: [businessId, "dashboard"] });
      });

      // Events published between the initial list fetch and the stream (re)opening
      // are never delivered — refetch on every open so that gap can't lose updates.
      es.onopen = () => {
        touch();
        refreshAll();
      };
      // EventSource auto-reconnects after loud errors; onopen refreshes on recovery.
      es.onerror = touch;
    };
    connect();

    // Watchdog: pings arrive every 25s; >65s of silence = half-dead pipe → rebuild.
    const watchdog = window.setInterval(() => {
      if (Date.now() - lastSeen > 65_000) connect();
    }, 10_000);

    // Waking the app (tab foregrounded, window focused, network back) must feel
    // instant: refetch now, and rebuild the stream if it's closed or stale.
    const onWake = () => {
      if (document.visibilityState !== "visible") return;
      refreshAll();
      if (!es || es.readyState === EventSource.CLOSED || Date.now() - lastSeen > 40_000) {
        connect();
      }
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);

    return () => {
      disposed = true;
      window.clearInterval(watchdog);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("online", onWake);
      es?.close();
    };
  }, [businessId, qc]);
}
